import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import SeoStructuredData from '@/components/feature/SeoStructuredData';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(id) : undefined;
  const relatedArticles = article ? getRelatedArticles(article) : [];
  const category = article ? categories.find((c) => c.slug === article.categorySlug) : undefined;

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-xl font-semibold text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-foreground-600 mb-4">抱歉，您访问的文章不存在或已被移除。</p>
            <Link
              to="/"
              className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <SeoStructuredData
        type="Article"
        articleTitle={article.title}
        articleDescription={article.excerpt}
        articleDate={article.publishDate}
        articleAuthor={article.author}
        articleImage={article.coverImage}
        pageUrl={`https://jiucao.community/article/${article.id}`}
      />
      <SeoStructuredData
        type="WebPage"
        pageTitle={`${article.title} - 久草社区`}
        pageDescription={article.excerpt}
        pageUrl={`https://jiucao.community/article/${article.id}`}
        breadcrumbItems={[
          { name: '首页', url: 'https://jiucao.community' },
          { name: article.category, url: `https://jiucao.community/category/${article.categorySlug}` },
          { name: article.title, url: `https://jiucao.community/article/${article.id}` },
        ]}
      />

      <Navbar />

      <main className="pt-16">
        <div className="w-full max-w-4xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.category, href: `/category/${article.categorySlug}` },
              { label: article.title },
            ]}
          />
        </div>

        <article>
          <div className="w-full max-w-4xl mx-auto px-4 md:px-6">
            <header className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700 hover:bg-accent-200 transition-colors"
                >
                  {article.category}
                </Link>
                {article.tags.slice(0, 2).map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full overflow-hidden bg-background-200">
                    <img src={article.authorAvatar} alt={article.author} className="w-full h-full object-cover" />
                  </div>
                  <span className="font-medium text-foreground-700">{article.author}</span>
                </div>
                <span className="text-foreground-400">|</span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.publishDate}
                </span>
                <span className="text-foreground-400">|</span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.readCount >= 10000
                    ? `${(article.readCount / 10000).toFixed(1)}万阅读`
                    : `${article.readCount}阅读`}
                </span>
                <span className="text-foreground-400">|</span>
                <span className="flex items-center gap-1">
                  <i className="ri-chat-3-line"></i>
                  {article.commentCount}评论
                </span>
                <span className="text-foreground-400">|</span>
                <span className="flex items-center gap-1">
                  <i className="ri-heart-line"></i>
                  {article.likeCount}点赞
                </span>
              </div>
            </header>

            <div className="w-full mb-8 rounded-xl overflow-hidden">
              <div className="w-full h-[300px] md:h-[420px]">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div
              className="article-content pb-8 border-b border-background-200/60"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex flex-wrap items-center gap-2 mt-6 pb-8 border-b border-background-200/60">
              <span className="text-sm text-foreground-500 mr-2">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700 cursor-pointer hover:bg-secondary-200 transition-colors"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="flex items-center justify-between py-6">
              <div className="flex items-center gap-4">
                <button className="flex items-center gap-2 px-4 py-2 rounded-full border border-background-200/60 text-sm text-foreground-600 hover:bg-primary-50 hover:text-primary-500 hover:border-primary-200 transition-colors whitespace-nowrap">
                  <i className="ri-heart-line"></i>
                  点赞 ({article.likeCount})
                </button>
                <button className="flex items-center gap-2 px-4 py-2 rounded-full border border-background-200/60 text-sm text-foreground-600 hover:bg-primary-50 hover:text-primary-500 hover:border-primary-200 transition-colors whitespace-nowrap">
                  <i className="ri-bookmark-line"></i>
                  收藏
                </button>
                <button className="flex items-center gap-2 px-4 py-2 rounded-full border border-background-200/60 text-sm text-foreground-600 hover:bg-primary-50 hover:text-primary-500 hover:border-primary-200 transition-colors whitespace-nowrap">
                  <i className="ri-share-forward-line"></i>
                  分享
                </button>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100/70 py-10 md:py-14">
            <div className="w-full max-w-4xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl font-bold text-foreground-950 mb-6">相关推荐</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((rel) => (
                  <ArticleCard key={rel.id} article={rel} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}